import React, { useMemo, useState } from 'react';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import classNames from 'classnames';
import ClickableTableRow from '@/shared/components/ClickableTableRow';
import { ApyPeriodSelect } from '@/shared/components/lending/ApyPeriodSelect';
import BorrowRewardsTooltip from '@/shared/components/lending/BorrowRewardsTooltip';
import { IncentivisedApyText } from '@/shared/components/lending/IncentivisedApyText';
import SupplyRewardsTooltip from '@/shared/components/lending/SupplyRewardsTooltip';
import { BoostIcon } from '@/shared/components/logo/BoostPoolLogo';
import { ChainLogo } from '@/shared/components/logo/ChainLogo';
import { TokenLogo } from '@/shared/components/logo/TokenLogo';
import { TokenWithChain } from '@/shared/components/logo/TokenWithChain';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import { Tooltip } from '@/shared/components/molecules/Tooltip';
import { useIncentives } from '@/shared/hooks/useIncentives';
import { useLendingPools } from '@/shared/hooks/useLendingPools';
import useTableSort from '@/shared/hooks/useTableSort';
import { apyToText } from '@/shared/utils/boost';
import { chainLabel } from '@/shared/utils/chainflip';
import { chainflipAssetMap } from '@/shared/utils/env';
import { COMPARATORS } from '@/shared/utils/helpers';
import {
  type ApyAveragePeriodOption,
  type BorrowApyPeriodOption,
  BORROW_APY_PERIODS,
  DEFAULT_APY_PERIOD,
  SUPPLY_APY_PERIODS,
} from '@/shared/utils/lending';
import { formatUsdValue } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';

type PoolRowProps = {
  asset: ChainflipAsset;
  apy: number;
  suppliedAmount: TokenAmount;
  suppliedAmountUsd: string;
  borrowedAmount: TokenAmount;
  borrowedAmountUsd: string;
  borrowRate: number;
  utilisationRatePercent: number;
  Button: React.FC<{ asset: ChainflipAsset }>;
  onRowClick?: (asset: ChainflipAsset) => void;
  supplyRewardsApy: number;
  borrowRewardsApy: number;
  seasonIndex: number;
  supplyDailyPoints: number;
  borrowDailyPoints: number;
  isPoolsLoading: boolean;
  isIncentivesLoading: boolean;
};

const PoolRow = ({
  asset,
  apy,
  suppliedAmount,
  suppliedAmountUsd,
  borrowedAmount,
  borrowedAmountUsd,
  borrowRate,
  utilisationRatePercent,
  Button,
  onRowClick,
  supplyRewardsApy,
  borrowRewardsApy,
  seasonIndex,
  supplyDailyPoints,
  borrowDailyPoints,
  isPoolsLoading,
  isIncentivesLoading,
}: PoolRowProps) => (
  <ClickableTableRow
    disableHoverStyle={!onRowClick}
    onClick={() => onRowClick?.(asset)}
    className={classNames(onRowClick && 'h-[64px]')}
  >
    <td>
      <div className="flex flex-col gap-y-1">
        <div className="flex items-center gap-x-2 font-aeonikMedium text-cf-white">
          <div className="relative h-[37px] w-[35px]">
            <div className="absolute bottom-px">
              <TokenWithChain
                chainLogo={<ChainLogo chain={chainflipAssetMap[asset].chain} />}
                tokenLogo={<TokenLogo size="large" token={asset} />}
                size="large"
              />
            </div>
          </div>
          <div className="font-aeonikMedium">
            <div className="flex items-center gap-x-1">
              <span className="text-white">{chainflipAssetMap[asset].symbol}</span>
              {asset === 'Btc' && (
                <Tooltip content="This market is funding Boost.">
                  <BoostIcon width={16} height={16} />
                </Tooltip>
              )}
            </div>
            <div className="text-cf-light-2">
              on {chainLabel(chainflipAssetMap[asset].chain.chainflipId)}
            </div>
          </div>
        </div>
      </div>
    </td>
    <td>
      <IncentivisedApyText
        tooltipContent={
          <SupplyRewardsTooltip
            rewardsApy={supplyRewardsApy}
            baseApy={apy}
            seasonIndex={seasonIndex}
            dailyPoints={supplyDailyPoints}
          />
        }
        baseApy={apy}
        rewardsApy={supplyRewardsApy}
        totalApy={apy + supplyRewardsApy}
        isLoading={isPoolsLoading || isIncentivesLoading}
        showBreakdown
      />
    </td>
    <td>
      <div className="flex flex-col font-aeonikMedium">
        <span className="flex gap-x-1 text-cf-white">
          {suppliedAmount.toFormatted()} {chainflipAssetMap[asset].symbol}
        </span>
        <span className="text-cf-light-2">{formatUsdValue(suppliedAmountUsd)}</span>
      </div>
    </td>
    <td>
      <div className="flex flex-col font-aeonikMedium">
        <span className="flex gap-x-1 text-cf-white">
          {borrowedAmount.toFormatted()} {chainflipAssetMap[asset].symbol}
        </span>
        <span className="text-cf-light-2">{formatUsdValue(borrowedAmountUsd)}</span>
      </div>
    </td>
    <td>
      <IncentivisedApyText
        tooltipContent={
          <BorrowRewardsTooltip
            rewardsApy={borrowRewardsApy}
            baseApy={borrowRate}
            seasonIndex={seasonIndex}
            dailyPoints={borrowDailyPoints}
          />
        }
        baseApy={borrowRate}
        rewardsApy={borrowRewardsApy}
        totalApy={borrowRate - borrowRewardsApy}
        isLoading={isPoolsLoading || isIncentivesLoading}
        className="text-14"
        showBreakdown
        showNegativeRewards
      />
    </td>
    <td className="font-aeonikMedium text-14 text-cf-white">{apyToText(utilisationRatePercent)}</td>
    <td>
      <div className="flex justify-end">
        <Button asset={asset} />
      </div>
    </td>
  </ClickableTableRow>
);

const DEFAULT_SORT = { col: 2, direction: 'DESC' as 'ASC' | 'DESC' };

const SORT_FUNCTIONS: ((poolData: PoolRowProps) => number)[] = [
  () => 0, // pool name is not sortable
  (poolData) => Number(poolData.apy),
  (poolData) => Number(poolData.suppliedAmountUsd),
  (poolData) => Number(poolData.borrowedAmountUsd),
  (poolData) => Number(poolData.borrowRate),
  (poolData) => Number(poolData.utilisationRatePercent),
];

export const LendingPoolsTable = ({
  className,
  onRowClick,
  Button,
  poolAssets,
}: {
  className?: string;
  onRowClick?: (asset: ChainflipAsset) => void;
  Button: React.FC<{ asset: ChainflipAsset }>;
  poolAssets?: ChainflipAsset[];
}) => {
  const { lendingPools, isLoading: isPoolsLoading } = useLendingPools();
  const [supplyApyPeriod, setSupplyApyPeriod] =
    useState<ApyAveragePeriodOption>(DEFAULT_APY_PERIOD);
  const [borrowApyPeriod, setBorrowApyPeriod] = useState<BorrowApyPeriodOption>(DEFAULT_APY_PERIOD);

  const { isLoading: isIncentivesLoading, activeSeason } = useIncentives();

  const { setCol, sort } = useTableSort({
    initialSort: DEFAULT_SORT,
  });

  const sortedRows = useMemo(
    () =>
      lendingPools
        .filter((pool) => (poolAssets ? poolAssets.includes(pool.asset) : true))
        .map((pool) => {
          const borrowIncentive = activeSeason?.pools.find(
            (incentive) => incentive.asset === pool.asset && incentive.type === 'BORROW',
          );
          const supplyIncentive = activeSeason?.pools.find(
            (incentive) => incentive.asset === pool.asset && incentive.type === 'SUPPLY',
          );
          return {
            asset: pool.asset,
            apy: pool.projectedSupplyApyByPeriod[supplyApyPeriod.value],
            borrowRate: pool.projectedBorrowApyByPeriod[borrowApyPeriod.value],
            utilisationRatePercent: pool.utilisationRateBps / 100,
            suppliedAmount: TokenAmount.fromAsset(pool.totalAmount, pool.asset),
            suppliedAmountUsd: pool.totalAmountUsd,
            borrowedAmount: TokenAmount.fromAsset(pool.totalBorrowedAmount, pool.asset),
            borrowedAmountUsd: pool.totalBorrowedAmountUsd,
            Button,
            onRowClick,
            supplyRewardsApy: supplyIncentive?.apy ?? 0,
            borrowRewardsApy: borrowIncentive?.apy ?? 0,
            seasonIndex: activeSeason?.seasonIndex ?? 0,
            supplyDailyPoints: supplyIncentive?.dailyPoints ?? 0,
            borrowDailyPoints: borrowIncentive?.dailyPoints ?? 0,
            isPoolsLoading,
            isIncentivesLoading,
          };
        })
        .toSorted((a, b) =>
          COMPARATORS[sort.direction](SORT_FUNCTIONS[sort.col](a), SORT_FUNCTIONS[sort.col](b)),
        )
        .map((data) => (
          <PoolRow key={String(data.asset)} {...data} onRowClick={onRowClick} Button={Button} />
        )),
    [
      lendingPools,
      sort,
      Button,
      onRowClick,
      activeSeason,
      poolAssets,
      isIncentivesLoading,
      isPoolsLoading,
      supplyApyPeriod,
      borrowApyPeriod,
    ],
  );

  return (
    <TableV2
      isLoading={isPoolsLoading || isIncentivesLoading}
      className={className}
      thClassName="px-1 [&:first-child]:pl-4 [&:last-child]:pr-4"
      tbodyClassName="[&>tr>td:first-child]:pl-4 [&>tr>td:last-child]:pr-4 [&>tr>td]:px-1 [&>tr>td]:py-4"
      columns={[
        { name: 'Asset', sortable: false },
        {
          sortable: true,
          tooltip: `Annual Percentage Yield (APY) estimates the annual return in a lending pool. We take hourly snapshots of fees earned in the pool, average them over the past ${supplyApyPeriod.description}, and project the result over a year.`,
          name: 'Supply APY',
          icon: (
            <ApyPeriodSelect
              periods={SUPPLY_APY_PERIODS}
              selected={supplyApyPeriod}
              onChange={setSupplyApyPeriod}
            />
          ),
        },
        {
          name: 'Supplied',
          tooltip: 'The amount currently supplied to this market.',
          sortable: true,
        },
        {
          tooltip: 'The amount currently lent out to borrowers in this market.',
          name: 'Borrowed',
          sortable: true,
        },
        {
          tooltip:
            borrowApyPeriod.value === 'CURRENT'
              ? 'The annualized borrowing rate this market is charging right now, based on its current utilisation.'
              : `The average annualized borrowing rate across the market over the past ${borrowApyPeriod.description}.`,
          name: 'Borrow Rate',
          sortable: true,
          icon: (
            <ApyPeriodSelect
              periods={BORROW_APY_PERIODS}
              selected={borrowApyPeriod}
              onChange={setBorrowApyPeriod}
            />
          ),
        },
        {
          tooltip: 'The percentage of supplied assets that is currently borrowed.',
          name: 'Utilisation Rate',
          sortable: true,
        },
        { name: '', sortable: false },
      ]}
      rows={sortedRows}
      sorting={{ setCol, sort }}
    />
  );
};
